/**
 *
 * AdminNavbar
 *
 */
import * as React from "react"
import { Link, useNavigate } from "react-router-dom"
import axios from "axios"
// reactstrap components
import {
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  DropdownToggle,
  Navbar,
  Nav,
  Container,
  Media,
} from "reactstrap"
import {
  removeLocalStorage,
  getLocalStorage,
} from "../../../utils/tokenstorage"
import { Auth } from "../../../utils/auth"

interface Props {
  brandText: String
}

// TypeScript interface for notifications
interface Notification {
  id: number
  type: string
  title: string
  message: string
  is_read: boolean
  reference_type: string
  reference_id: number
  created_at: string
}

// Helper to get fresh auth headers (prevents stale token issues)
const getAuthHeaders = () => ({
  Authorization: "Bearer " + getLocalStorage()?.token,
})

const timeAgo = (date: string) => {
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000)
  if (seconds < 60) return "just now"
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

export function AdminNavbar(props: Props) {
  const navigate = useNavigate()
  const [unreadCount, setUnreadCount] = React.useState(0)
  const [notifications, setNotifications] = React.useState<Notification[]>([])
  const [notifOpen, setNotifOpen] = React.useState(false)

  // Memoized fetch function that always gets fresh token
  const fetchUnreadCount = React.useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/notifications/unread-count`,
        { headers: getAuthHeaders() },
      )
      setUnreadCount(res.data?.unread_count || 0)
    } catch (error) {
      // silently fail
    }
  }, [])

  // Fetch notifications with fresh token
  const fetchNotifications = React.useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/notifications?limit=10`,
        { headers: getAuthHeaders() },
      )
      setNotifications(res.data?.data || [])
    } catch (error) {
      // silently fail
    }
  }, [])

  // TODO: Re-enable when notification features are complete
  // React.useEffect(() => {
  //   fetchUnreadCount()
  //   const interval = setInterval(fetchUnreadCount, 30000)
  //   return () => clearInterval(interval)
  // }, [fetchUnreadCount])

  const handleNotifToggle = React.useCallback(() => {
    setNotifOpen(prev => {
      if (!prev) fetchNotifications()
      return !prev
    })
  }, [fetchNotifications])

  const handleMarkAllRead = React.useCallback(async () => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BASE_API_URL}/notifications/read-all`,
        {},
        { headers: getAuthHeaders() },
      )
      setUnreadCount(0)
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
    } catch (error) {
      // silently fail
    }
  }, [])

  const handleNotifClick = React.useCallback(
    async (notif: Notification) => {
      // Mark as read
      if (!notif.is_read) {
        try {
          await axios.put(
            `${process.env.REACT_APP_BASE_API_URL}/notifications/${notif.id}/read`,
            {},
            { headers: getAuthHeaders() },
          )
          setUnreadCount(prev => Math.max(0, prev - 1))
          setNotifications(prev =>
            prev.map(n => (n.id === notif.id ? { ...n, is_read: true } : n)),
          )
        } catch (error) {
          // silently fail
        }
      }
      // Navigate based on type
      if (notif.reference_type === "customer_inquiry") {
        navigate("/admin/customer-service")
      } else if (notif.reference_type === "correction_request") {
        navigate("/admin/correction-approvals")
      }
      setNotifOpen(false)
    },
    [navigate],
  )

  const getNotifIcon = (type: string) => {
    switch (type) {
      case "customer_inquiry":
        return "ni ni-chat-round"
      case "correction_approved":
        return "ni ni-check-bold"
      case "correction_rejected":
        return "ni ni-fat-remove"
      default:
        return "ni ni-bell-55"
    }
  }

  const getNotifColor = (type: string) => {
    switch (type) {
      case "customer_inquiry":
        return "#f5365c"
      case "correction_approved":
        return "#2dce89"
      case "correction_rejected":
        return "#fb6340"
      default:
        return "#5e72e4"
    }
  }

  const handleLogout = event => {
    event.preventDefault()
    removeLocalStorage()
    navigate("/auth/login")
  }

  const getInitials = (name: string | null) => {
    if (!name) return "U"
    const names = name.trim().split(" ")
    if (names.length === 1) return names[0].substring(0, 2).toUpperCase()
    return (names[0][0] + names[names.length - 1][0]).toUpperCase()
  }

  return (
    <>
      <Navbar
        className="navbar-top"
        expand="md"
        id="navbar-main"
        style={{
          background: "#1a1a1a",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          padding: "12px 0",
        }}
      >
        <Container fluid>
          <Link
            className="d-none d-lg-inline-block"
            to="/"
            style={{ textDecoration: "none" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  background:
                    "linear-gradient(135deg, #f5365c 0%, #d63031 100%)",
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 12px rgba(245, 54, 92, 0.3)",
                }}
              >
                <i
                  className="ni ni-delivery-fast"
                  style={{ fontSize: "20px", color: "#fff" }}
                />
              </div>
              <div>
                <h3
                  style={{
                    color: "#fff",
                    fontSize: "20px",
                    fontWeight: "700",
                    marginBottom: "0",
                    letterSpacing: "0.5px",
                  }}
                >
                  SPEEDWORKS
                </h3>
              </div>
            </div>
          </Link>
          <Nav className="align-items-center d-none d-md-flex" navbar>
            {/* TODO: Re-enable notification bell when Customer Service and Correction Approvals features are complete */}

            {/* User Menu */}
            <UncontrolledDropdown nav>
              <DropdownToggle className="pr-0" nav>
                <Media
                  className="align-items-center"
                  style={{ cursor: "pointer" }}
                >
                  <span
                    className="avatar avatar-sm rounded-circle d-flex align-items-center justify-content-center text-white font-weight-bold"
                    style={{
                      background:
                        "linear-gradient(135deg, #f5365c 0%, #d63031 100%)",
                      fontSize: "0.75rem",
                      letterSpacing: "0.5px",
                      width: "40px",
                      height: "40px",
                      boxShadow: "0 4px 12px rgba(245, 54, 92, 0.3)",
                    }}
                  >
                    {getInitials(Auth.username())}
                  </span>
                  <Media className="ml-2 d-none d-lg-block">
                    <span
                      className="mb-0 font-weight-bold"
                      style={{
                        color: "#fff",
                        fontSize: "14px",
                      }}
                    >
                      {Auth.username()}
                    </span>
                  </Media>
                </Media>
              </DropdownToggle>
              <DropdownMenu
                className="dropdown-menu-arrow"
                right
                style={{
                  borderRadius: "12px",
                  border: "none",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                  marginTop: "8px",
                }}
              >
                <DropdownItem
                  className="noti-title"
                  header
                  tag="div"
                  style={{
                    background:
                      "linear-gradient(135deg, #f5365c 0%, #d63031 100%)",
                    borderRadius: "12px 12px 0 0",
                    padding: "12px 20px",
                  }}
                >
                  <h6
                    className="text-overflow m-0"
                    style={{ color: "#fff", fontWeight: "600" }}
                  >
                    Welcome!
                  </h6>
                </DropdownItem>
                <DropdownItem
                  to="/admin"
                  tag={Link}
                  style={{ padding: "12px 20px" }}
                >
                  <i className="ni ni-single-02" style={{ color: "#f5365c" }} />
                  <span style={{ marginLeft: "12px", color: "#32325d" }}>
                    My profile
                  </span>
                </DropdownItem>
                <DropdownItem divider />
                <DropdownItem
                  href="#"
                  onClick={e => handleLogout(e)}
                  style={{ padding: "12px 20px" }}
                >
                  <i className="ni ni-user-run" style={{ color: "#f5365c" }} />
                  <span style={{ marginLeft: "12px", color: "#32325d" }}>
                    Logout
                  </span>
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          </Nav>
        </Container>
      </Navbar>
    </>
  )
}
